import { useMemo, useState, useRef, useEffect, createContext, useContext } from "react";
import { Form, FormField as BaseFormField, PrimaryButton, SecondaryButton } from "common/Forms";
import { InRow } from "common/Grid";
import { TextInput, SelectInput, PasswordInput } from "common/Forms/inputs";
import { Grid, GridCell } from "common/Grid/Grid";
import { useNavigate } from "react-router-dom";
import { roleOptions } from "Constant/Constant";
import { parseSalesUserErrors } from "./salesUser.schema";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en";

const FormErrorContext = createContext({});

const FormField = (props) => {
  const errors = useContext(FormErrorContext);
  const errorKey = Object.keys(errors).find(
    (k) =>
      k.split(".").pop() === props.id ||
      k.split(".").pop().toLowerCase() === props.id?.toLowerCase() ||
      k === props.id,
  );
  const { inputFactory, ...rest } = props;
  const wrappedInputFactory = (id, name, onValidation) => (
    <div className="text-black">{inputFactory(id, name, onValidation)}</div>
  );
  return (
    <BaseFormField
      {...rest}
      inputFactory={wrappedInputFactory}
      errorMessage={errorKey ? errors[errorKey] : undefined}
    />
  );
};

const emptyObj = {};
const trimStringValues = (values) =>
  Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value]),
  );

export const SalesUserForm = ({
  defaultValues = emptyObj,
  onSubmit,
  isLoading,
  isEdit = false,
}) => {
  const navigate = useNavigate();
  const [userInput, setUserInput] = useState({
    name: defaultValues.name || "",
    email: defaultValues.email || "",
    phone: defaultValues.phone || "",
    phonePrefix: defaultValues.phonePrefix || "+61",
    role: defaultValues.role || "Sales Person",
    password: defaultValues.password || "",
  });

  // ── Country prefix dropdown state ──
  const [prefixOpen, setPrefixOpen] = useState(false);
  const [prefixSearch, setPrefixSearch] = useState("");
  const prefixRef = useRef(null);
  const prefixSearchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!prefixOpen) return;
    const handler = (e) => {
      if (prefixRef.current && !prefixRef.current.contains(e.target)) {
        setPrefixOpen(false);
        setPrefixSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [prefixOpen]);

  // Auto-focus search when dropdown opens
  useEffect(() => {
    if (prefixOpen) {
      requestAnimationFrame(() => prefixSearchRef.current?.focus());
    }
  }, [prefixOpen]);

  const getFlagEmoji = (countryCode) =>
    countryCode
      .toUpperCase()
      .split("")
      .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
      .join("");

  const countryOptions = useMemo(
    () =>
      getCountries()
        .map((country) => ({
          code: country,
          name: en[country] || country,
          dialCode: `+${getCountryCallingCode(country)}`,
          flag: getFlagEmoji(country),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  const filteredPrefixOptions = useMemo(() => {
    if (!prefixSearch.trim()) return countryOptions;
    const q = prefixSearch.toLowerCase();
    return countryOptions.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dialCode.includes(q),
    );
  }, [prefixSearch, countryOptions]);

  const selectedCountryCode = useMemo(() => {
    if (!userInput.phonePrefix) return "";
    const match = countryOptions.find((c) => c.dialCode === userInput.phonePrefix);
    return match ? match.code : "";
  }, [userInput.phonePrefix, countryOptions]);

  // ── Form validation ──
  const [isSubmitted, setIsSubmitted] = useState(false);

  const formErrors = useMemo(() => {
    return isSubmitted ? parseSalesUserErrors(userInput, isEdit) : {};
  }, [userInput, isSubmitted, isEdit]);

  const isFormValid = useMemo(() => {
    const errors = parseSalesUserErrors(userInput, isEdit);
    return Object.keys(errors).length === 0;
  }, [userInput, isEdit]);

  const handleValidatedSubmit = () => {
    setIsSubmitted(true);
    const trimmedUserInput = trimStringValues(userInput);
    setUserInput(trimmedUserInput);
    const trimmedFormErrors = parseSalesUserErrors(trimmedUserInput, isEdit);
    if (Object.keys(trimmedFormErrors).length > 0) {
      return;
    }
    onSubmit(trimmedUserInput);
  };

  const textInputBoxClass = ["text_input_box"];

  return (
    <FormErrorContext.Provider value={formErrors}>
      <Form classNames={["my-6"]}>
        <Grid columns={2} rowGap={1.5} columnGap={2} classNames={["!gap-x-3 !gap-y-3"]}>
          <GridCell>
            <FormField
              id="name"
              label="Name"
              required={true}
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter name"
                  value={userInput.name}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, name: val ?? "" }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="email"
              label="Email"
              required={true}
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter email"
                  value={userInput.email}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, email: val ?? "" }))}
                />
              )}
            />
          </GridCell>

          {/* ── Phone field with prefix dropdown ── */}
          <GridCell>
            <FormField
              id="phone"
              label="Phone"
              required={true}
              classNames={textInputBoxClass}
              inputFactory={(id, name) => {
                const selected = selectedCountryCode
                  ? countryOptions.find((c) => c.code === selectedCountryCode)
                  : null;
                return (
                  <div
                    style={{ display: "flex", alignItems: "stretch", width: "100%", position: "relative" }}
                    ref={prefixRef}
                  >
                    {/* Trigger button */}
                    <button
                      type="button"
                      id="phonePrefix"
                      onClick={() => { setPrefixOpen((o) => !o); setPrefixSearch(""); }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        flexShrink: 0,
                        height: "39px",
                        padding: "0 10px",
                        border: "2px solid #e2e8f0",
                        borderRight: "none",
                        borderRadius: "4px 0 0 4px",
                        background: "#f8fafc",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#374151",
                        whiteSpace: "nowrap",
                        minWidth: "80px",
                        outline: "none",
                        userSelect: "none",
                      }}
                    >
                      <span style={{ fontSize: "18px", lineHeight: 1 }}>{selected ? selected.flag : "🌐"}</span>
                      <span style={{ fontSize: "13px" }}>{selected ? selected.dialCode : "Code"}</span>
                      <span style={{ fontSize: "9px", color: "#94a3b8", marginLeft: "2px" }}>{prefixOpen ? "▲" : "▼"}</span>
                    </button>

                    {/* Phone number input */}
                    <div style={{ flex: 1, overflow: "hidden", borderRadius: "0 4px 4px 0" }}>
                      <TextInput
                        id={id}
                        name={name}
                        placeholder="Enter phone number"
                        value={userInput.phone}
                        onChange={(_, val) => {
                          const digits = val.replace(/\D/g, "");
                          setUserInput((prev) => ({ ...prev, phone: digits }));
                        }}
                      />
                    </div>

                    {/* Dropdown panel */}
                    {prefixOpen && (
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(100% + 4px)",
                          left: 0,
                          zIndex: 1000,
                          background: "#fff",
                          border: "1.5px solid #e2e8f0",
                          borderRadius: "8px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          width: "300px",
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                        }}
                      >
                        {/* Search box */}
                        <div style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
                          <input
                            ref={prefixSearchRef}
                            type="text"
                            placeholder="Search country or code…"
                            value={prefixSearch}
                            onChange={(e) => setPrefixSearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            style={{
                              width: "100%",
                              padding: "6px 10px",
                              border: "1.5px solid #e2e8f0",
                              borderRadius: "6px",
                              fontSize: "13px",
                              outline: "none",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        {/* Country list */}
                        <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                          {filteredPrefixOptions.length === 0 ? (
                            <div style={{ padding: "12px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>
                              No country found
                            </div>
                          ) : (
                            filteredPrefixOptions.map((c) => (
                              <button
                                key={c.code}
                                type="button"
                                onClick={() => {
                                  setUserInput((prev) => ({ ...prev, phonePrefix: c.dialCode }));
                                  setPrefixOpen(false);
                                  setPrefixSearch("");
                                }}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  width: "100%",
                                  padding: "8px 12px",
                                  border: "none",
                                  background: selectedCountryCode === c.code ? "#f0f9ff" : "transparent",
                                  cursor: "pointer",
                                  fontSize: "13px",
                                  textAlign: "left",
                                  color: "#374151",
                                }}
                                onMouseEnter={(e) => {
                                  if (selectedCountryCode !== c.code) e.currentTarget.style.background = "#f8fafc";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background =
                                    selectedCountryCode === c.code ? "#f0f9ff" : "transparent";
                                }}
                              >
                                <span style={{ fontSize: "18px", lineHeight: 1, flexShrink: 0 }}>{c.flag}</span>
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {c.name}
                                </span>
                                <span style={{ color: "#64748b", fontWeight: 600, flexShrink: 0 }}>{c.dialCode}</span>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="role"
              label="Role"
              required={true}
              inputFactory={(id, name) => (
                <SelectInput
                  id={id}
                  name={name}
                  disabled={isEdit}
                  placeholder="Select role"
                  selectedKey={userInput.role}
                  optionProvider={() => roleOptions}
                  keySelector={(x) => `${x.value}`}
                  isClearable={false}
                  labelSelector={(x) => `${x.label}`}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({ ...prev, role: n ? n.value : "Sales Person" }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="password"
              label="Password"
              required={true}
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <PasswordInput
                  id={id}
                  name={name}
                  placeholder="Enter password"
                  value={userInput.password}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, password: val }))}
                />
              )}
            />
          </GridCell>
        </Grid>

        <InRow gap={1} classNames={["justify-center mt-8"]}>
          <PrimaryButton
            classNames={["min-w-[180px]"]}
            disabled={isLoading}
            action={handleValidatedSubmit}>
            {isEdit ? "Update Sales User" : "Create Sales User"}
          </PrimaryButton>
          <SecondaryButton classNames={["min-w-[180px]"]} action={() => navigate("/sales-user")}>
            Cancel
          </SecondaryButton>
        </InRow>
      </Form>
    </FormErrorContext.Provider>
  );
};
